if (!customElements.get('bf-product-info')) {
  customElements.define(
    'bf-product-info',
    class BFProductInfo extends HTMLElement {
      connectedCallback() {
        this.variants = this.readJSON('[data-bf-product-variants]', []);
        this.config = this.readJSON('[data-bf-product-config]', {});
        this.optionInputs = Array.from(this.querySelectorAll('[data-bf-option-input]'));
        this.variantInputs = Array.from(this.querySelectorAll('[data-bf-product-variant-id]'));
        this.price = this.querySelector('[data-bf-current-price]');
        this.compareAtPrice = this.querySelector('[data-bf-current-compare-price]');
        this.availability = this.querySelector('[data-bf-current-availability]');
        this.metadata = this.querySelector('[data-bf-variant-metadata]');
        this.addButton = this.querySelector('[data-bf-add-to-cart]');
        this.addButtonLabel = this.querySelector('[data-bf-add-to-cart-label]');
        this.buyNow = this.querySelector('[data-bf-buy-now]');
        this.gallery = this.querySelector('bf-product-gallery');
        this.onOptionChange = (event) => this.handleOptionChange(event);

        this.optionInputs.forEach((input) => input.addEventListener('change', this.onOptionChange));

        const initialId = Number(this.variantInputs[0]?.value || 0);
        const initialVariant = this.variants.find((variant) => Number(variant.id) === initialId) || this.findVariant();
        this.update(initialVariant, true);
      }

      disconnectedCallback() {
        this.optionInputs?.forEach((input) => input.removeEventListener('change', this.onOptionChange));
      }

      readJSON(selector, fallback) {
        const node = this.querySelector(selector);
        if (!node) return fallback;
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error('Bike Finder product data could not be parsed.', error);
          return fallback;
        }
      }

      handleOptionChange(event) {
        if (!event.target.matches('[data-bf-option-input]')) return;
        this.update(this.findVariant(), true);
      }

      findVariant() {
        const selectedOptions = [];
        this.optionInputs.forEach((input) => {
          if (input.checked) selectedOptions[Number(input.dataset.optionPosition) - 1] = input.value;
        });

        if (!selectedOptions.length || selectedOptions.some((value) => value === undefined)) return null;
        return (
          this.variants.find(
            (variant) =>
              variant.options.length === selectedOptions.length &&
              variant.options.every((option, index) => option === selectedOptions[index])
          ) || null
        );
      }

      update(variant, updateUrl) {
        this.currentVariant = variant || null;
        this.updateVariantInputs(variant);
        this.updatePrice(variant);
        this.updateAvailability(variant);
        this.updatePurchaseActions(variant);
        this.updateMedia(variant);
        if (updateUrl) this.updateURL(variant);

        this.dispatchEvent(
          new CustomEvent('bf:variant-change', {
            bubbles: true,
            detail: { variant: this.currentVariant },
          })
        );
      }

      updateVariantInputs(variant) {
        this.variantInputs.forEach((input) => {
          input.value = variant?.id || '';
          input.disabled = !variant;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updatePrice(variant) {
        if (this.price) {
          this.price.textContent = variant?.price || '—';
          this.price.classList.toggle('bf-product-main__price--empty', !variant);
        }

        if (this.compareAtPrice) {
          const showCompareAt = Boolean(
            variant && variant.compareAtPrice && Number(variant.compareAtPriceCents) > Number(variant.priceCents)
          );
          this.compareAtPrice.textContent = showCompareAt ? variant.compareAtPrice : '';
          this.compareAtPrice.hidden = !showCompareAt;
        }

        if (this.metadata) {
          const sku = variant?.sku || this.config.skuMissingLabel || 'Not configured';
          this.metadata.textContent = variant ? `SKU: ${sku}. Weight: ${variant.weightLabel}.` : '';
        }
      }

      updateAvailability(variant) {
        if (!this.availability) return;
        const state = !variant ? 'unavailable' : variant.available ? 'available' : 'sold-out';
        this.availability.textContent =
          state === 'available'
            ? this.config.availableLabel
            : state === 'sold-out'
              ? this.config.soldOutLabel
              : this.config.unavailableLabel;
        this.availability.hidden = state === 'available';
        this.availability.classList.toggle('is-available', state === 'available');
        this.availability.dataset.state = state;
        this.closest('[data-bf-product-main]')?.setAttribute('data-bf-product-state', state);
      }

      updatePurchaseActions(variant) {
        const canPurchase = Boolean(variant?.available);
        if (this.addButton) {
          this.addButton.disabled = !canPurchase;
          this.addButton.setAttribute('aria-disabled', String(!canPurchase));
        }
        if (this.addButtonLabel) {
          this.addButtonLabel.textContent = canPurchase
            ? this.config.addToCartLabel
            : variant
              ? this.config.soldOutLabel
              : this.config.unavailableLabel;
        }

        if (this.buyNow) {
          this.buyNow.disabled = !canPurchase;
          this.buyNow.setAttribute('aria-disabled', String(!canPurchase));
        }
      }

      updateMedia(variant) {
        if (!variant?.featuredMediaId) return;
        this.gallery?.selectMediaById?.(String(variant.featuredMediaId), true);
      }

      updateURL(variant) {
        const url = new URL(window.location.href);
        if (variant?.id) url.searchParams.set('variant', variant.id);
        else url.searchParams.delete('variant');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }
    }
  );
}
